import {
  Building2,
  CalendarDays,
  ChefHat,
  ClipboardList,
  FileText,
  HelpCircle,
  LayoutDashboard,
  type LucideIcon,
  PackageCheck,
  Receipt,
  Settings,
  Truck,
  UtensilsCrossed,
  Wallet,
} from 'lucide-react';

import { hasPermission } from '../auth/has-permission';
import type { CurrentUser } from '../auth/use-current-user';

export type NavPermission =
  | 'dashboard.view'
  | 'order.read'
  | 'production.read'
  | 'deliverynote.read'
  | 'deliverynote.sign'
  | 'invoice.read'
  | 'payment.read'
  | 'payment.register'
  | 'payment.void'
  | 'fiscal.config'
  | 'email.config'
  | 'customer.read'
  | 'contract.read'
  | 'menu.read'
  | 'menu.calendar.manage'
  | 'settings.read'
  | 'settings.manage'
  | 'user.read'
  | 'role.read';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * `null`: visible to every signed-in user (it exposes no business data).
   * An array means "visible with any one of these" — the hub's single
   * sidebar entry needs that, since no single permission covers everyone
   * who has access to at least one of its five cards (back-office-shell/spec.md).
   */
  permission: NavPermission | readonly NavPermission[] | null;
}

export interface NavGroup {
  id: string;
  label: string;
  items: readonly NavItem[];
}

const dashboardItem: NavItem = {
  to: '/',
  label: 'Tablero del día',
  icon: LayoutDashboard,
  permission: 'dashboard.view',
};
const ordersItem: NavItem = { to: '/orders', label: 'Pedidos', icon: ClipboardList, permission: 'order.read' };
const productionItem: NavItem = {
  to: '/production',
  label: 'Producción',
  icon: ChefHat,
  permission: 'production.read',
};
const deliveryNotesItem: NavItem = {
  to: '/delivery-notes',
  label: 'Remitos',
  icon: Truck,
  permission: 'deliverynote.read',
};
const myDeliveriesItem: NavItem = {
  to: '/entregas',
  label: 'Mis entregas',
  icon: PackageCheck,
  permission: 'deliverynote.sign',
};
const invoicesItem: NavItem = {
  to: '/invoices',
  label: 'Facturación',
  icon: Receipt,
  permission: 'invoice.read',
};
/** `back-office-shell/spec.md` → "Cobranzas visible junto a Facturación para BILLING": part of the "Facturación" group, gated on `payment.read` alone (registering/voiding are per-action permissions inside the screen, not the sidebar entry). */
const paymentsItem: NavItem = {
  to: '/payments',
  label: 'Cobranzas',
  icon: Wallet,
  permission: 'payment.read',
};
const customersItem: NavItem = {
  to: '/customers',
  label: 'Clientes',
  icon: Building2,
  permission: 'customer.read',
};
const contractsItem: NavItem = {
  to: '/contracts',
  label: 'Contratos',
  icon: FileText,
  permission: 'contract.read',
};
const menusItem: NavItem = { to: '/menus', label: 'Catálogo', icon: UtensilsCrossed, permission: 'menu.read' };
const calendarItem: NavItem = {
  to: '/calendar',
  label: 'Calendario',
  icon: CalendarDays,
  permission: 'menu.calendar.manage',
};
/**
 * Replaces the former `arcaConfigItem`/`fiscalPointsOfSaleItem` pair: both
 * routes still exist and are still reachable by direct URL
 * (`design.md` §9.1), they just no longer have their own sidebar entry.
 * Visible with any one of the five distinct permissions that unlock a card on the
 * hub (`settings-hub/spec.md`), not just `settings.read` alone — a role
 * whose only hub permission is `role.read` still needs to reach `/settings`.
 */
const configuracionItem: NavItem = {
  to: '/settings',
  label: 'Configuración',
  icon: Settings,
  permission: ['fiscal.config', 'email.config', 'settings.read', 'settings.manage', 'user.read', 'role.read'],
};

/** Outside every group, pinned to the sidebar footer — same spot it occupies today. */
export const GUIDE_ITEM: NavItem = { to: '/guia', label: 'Guía', icon: HelpCircle, permission: null };

/** 4 fixed thematic groups, in display order (back-office-shell/spec.md). */
export const NAVIGATION_GROUPS: readonly NavGroup[] = [
  {
    id: 'operational',
    label: 'Operación diaria',
    items: [dashboardItem, ordersItem, productionItem, deliveryNotesItem, myDeliveriesItem],
  },
  { id: 'billing', label: 'Facturación', items: [invoicesItem, paymentsItem] },
  {
    id: 'masters',
    label: 'Maestros',
    items: [customersItem, contractsItem, menusItem, calendarItem],
  },
  { id: 'settings', label: 'Configuración', items: [configuracionItem] },
];

/** Icon mapping from `design.md` §2. Flattened for callers that only need the flat list. */
export const NAVIGATION: readonly NavItem[] = [
  ...NAVIGATION_GROUPS.flatMap((group) => group.items),
  GUIDE_ITEM,
];

export function isNavItemVisible(item: NavItem, currentUser: CurrentUser | undefined): boolean {
  if (item.permission === null) {
    return true;
  }

  const permissions: readonly NavPermission[] = Array.isArray(item.permission)
    ? item.permission
    : [item.permission];

  return permissions.some((permission) => hasPermission(currentUser, permission));
}

export interface VisibleNavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

/** Purely presentational filtering — a group disappears once none of its items are visible. */
export function visibleNavGroups(
  groups: readonly NavGroup[],
  currentUser: CurrentUser | undefined,
): VisibleNavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isNavItemVisible(item, currentUser)),
    }))
    .filter((group) => group.items.length > 0);
}
